import {DescService} from "@bufbuild/protobuf";
import { createConnectTransport } from "@connectrpc/connect-web";
import { createClient,Client} from "@connectrpc/connect";
import { Interceptor } from "@connectrpc/connect";
import { useMemo } from "react";
import { tokenKey } from "./constant";
import { toast } from "react-toastify";

const shouldLogRequests = process.env.NODE_ENV === "development";

function logRequestStart(req: any): number {
    const startedAt = Date.now();
    const url = new URL(req.url, window.location.origin);
    console.info("[connect][request]", {
        method: req.method,
        url: url.toString(),
        service: req.service.typeName,
        procedure: req.method.name,
        headers: Object.fromEntries(req.header.entries()),
    });
    return startedAt;
}

function logRequestSuccess(req: any, startedAt: number, res: any) {
    const durationMs = Date.now() - startedAt;
    console.info("[connect][response]", {
        method: req.method,
        url: req.url,
        service: req.service.typeName,
        procedure: req.method.name,
        durationMs,
        headers: Object.fromEntries(res.header.entries()),
        trailers: Object.fromEntries(res.trailer.entries()),
    });
}

function logRequestError(req: any, startedAt: number, error: any) {
    const durationMs = Date.now() - startedAt;
    console.error("[connect][error]", {
        method: req.method,
        url: req.url,
        service: req.service.typeName,
        procedure: req.method.name,
        durationMs,
        code: error?.code,
        message: error?.message,
        details: error?.details,
        rawMessage: error?.rawMessage,
        metadata: error?.metadata ? Object.fromEntries(error.metadata.entries()) : undefined,
        error,
    });
}

const requestLogger: Interceptor = (next) => async (req) => {
    const startedAt = shouldLogRequests ? logRequestStart(req) : 0;

    try {
        const res = await next(req);
        if (shouldLogRequests) {
            logRequestSuccess(req, startedAt, res);
        }
        return res;
    } catch (error: any) {
        if (shouldLogRequests) {
            logRequestError(req, startedAt, error);
        }
        throw error;
    }
};

const authInjector: Interceptor = (next) => async (req) => {
    const token = localStorage.getItem(tokenKey);
    req.header.set("X-MyTest-Client", `mytest-client`);
    if (token) {
        req.header.set("Authorization", `Bearer ${token}`);
    }

    try {
        return await next(req);
    } catch (error: any) {
        if (error?.code === 16) {
            toast.dismiss();
            toast.error("Token expired. Please log in again.");
            localStorage.removeItem(tokenKey);
            setTimeout(() => {
                window.location.href = '/login';
            }, 2000);
        }
        throw error;
    }
};

const baseUrl = process.env.REACT_APP_API_BASE_URL || '/';
const transport = createConnectTransport({
    // FIXME: we need to use production URL here for local debug, server sdie push relies on http/2.0, create-react-app proxy only supports http/1.1
    // baseUrl: "https://mdb-ng-production-admin-grpc.vendingontrack.com/",
    baseUrl: baseUrl,
    interceptors: [requestLogger, authInjector]
});


/**
 * Get a promise client for the given service.
 */
export function useClient<T extends DescService>(service: T): Client<T> {
    // We memoize the client, so that we only create one instance per service.
    return useMemo(() => createClient(service, transport), [service]);
}
